import { friendlyError } from '../../utils/userMessages';
import { brandPalette as palette, getGradientContrastText, getReadableColor } from '../../utils/brandPalette';
// src/components/dashboard/AdsManagement.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Switch,
    FormControlLabel,
    Menu,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    alpha,
    TablePagination,
    InputAdornment,
    CircularProgress,
    LinearProgress,
    Snackbar,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Image as ImageIcon,
    VideoCall as VideoCallIcon,
    Link as LinkIcon,
    Storefront as StorefrontIcon,
    TrendingUp as TrendingUpIcon,
    Menu as MoreVertIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    Timer as TimerIcon,
    Upload as UploadIcon,
    PlayCircleRounded as PlayCircleIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    CalendarToday as CalendarIcon,
    AttachMoney as MoneyIcon,
    Search as SearchIcon,
    Save as SaveIcon,
    Upload as FileUploadIcon,
    Delete as DeleteOutlineIcon,
    AutoAwesome as AiIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../components/auth/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    doc,
    query,
    where,
    onSnapshot,
    serverTimestamp,
    addDoc,
    updateDoc,
    deleteDoc,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header/Header';
import { uploadFile, deleteFile, resolveAuthenticatedCompanyId, validateUploadFile, UPLOAD_LIMITS } from '../../services/integrations/storage';
import { resolveCompanyId } from '../../utils/companyIdentity';

// ============================================
// DESIGN TOKENS - Enhanced Glassmorphism
// ============================================



const normalizeSafeWebUrl = (value = '') => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
        const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('unsafe protocol');
        return parsed.toString();
    } catch (_) {
        throw new Error('Enter a valid HTTP/HTTPS destination URL.');
    }
};

// ============================================
// RGBA HELPER
// ============================================

const rgba = (color, opacity) => {
    if (!color) return `rgba(0,0,0,${opacity})`;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ============================================
// GLASS WELL HELPER
// ============================================

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

// ============================================
// GLASS STYLES
// ============================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color,
    background: alpha(color, .07),
    border: `1px solid ${alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? '#fff' : color,
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color,
    background: 'rgba(255,253,250,.92)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    color: '#fff',
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: '#fff' },
});

// ============================================
// FLOATING ORBS
// ============================================

const FloatingOrbs = () => {
    const orbs = [
        { color: palette.rust, size: 520, top: '-10%', left: '4%', duration: 28, delay: 0 },
        { color: palette.pine, size: 440, top: '15%', left: '78%', duration: 34, delay: 2 },
        { color: palette.gold, size: 380, top: '55%', left: '-8%', duration: 26, delay: 1 },
        { color: palette.purple, size: 400, top: '70%', left: '68%', duration: 32, delay: 3 },
        { color: palette.teal, size: 320, top: '35%', left: '44%', duration: 30, delay: 1.5 },
    ];

    return (
        <Box
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
            }}
        >
            {orbs.map((orb, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{
                        opacity: [0.4, 0.7, 0.4],
                        x: [0, 50, -40, 0],
                        y: [0, -45, 35, 0],
                        scale: [1, 1.15, 0.9, 1],
                    }}
                    transition={{
                        duration: orb.duration,
                        delay: orb.delay,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{
                        position: 'absolute',
                        top: orb.top,
                        left: orb.left,
                        width: orb.size,
                        height: orb.size,
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${rgba(orb.color, 0.12)}, ${rgba(orb.color, 0.02)} 70%, transparent 100%)`,
                        filter: 'blur(12px)',
                        willChange: 'transform, opacity',
                    }}
                />
            ))}

            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    linear-gradient(${rgba(palette.ink, 0.015)} 1px, transparent 1px),
                    linear-gradient(90deg, ${rgba(palette.ink, 0.015)} 1px, transparent 1px)
                `,
                backgroundSize: '64px 64px',
                opacity: 0.4,
            }} />
        </Box>
    );
};

// ============================================
// SECTION RULE
// ============================================

const SectionRule = ({ color = palette.rust, width = 44 }) => (
    <Box
        sx={{
            width,
            height: 3,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${color}, ${rgba(color, 0)})`,
            mt: 0.75,
            animation: 'shimmerRule 3.2s ease-in-out infinite',
        }}
    />
);

// ============================================
// GLASS STAT CARD
// ============================================

const GlassStatCard = ({ title, value, icon, color, delay, subtitle }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    ...glassCard({ level: 2, radius: '20px', accent: color }),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    transition: `transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        ...glassLift(4, color),
                        '& .stat-glow': { opacity: 0.8, transform: 'scale(1.25)' },
                        '& .stat-pod': { transform: 'scale(1.08) rotate(-4deg)' },
                        '& .stat-sheen': { animation: `tfSheen 0.9s cubic-bezier(0.34,1.56,0.64,1)` },
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '8%',
                        right: '8%',
                        height: '2px',
                        borderRadius: '0 0 999px 999px',
                        background: `linear-gradient(90deg, transparent, ${rgba(color, 0.85)}, transparent)`,
                        pointerEvents: 'none',
                        zIndex: 2,
                    },
                }}
            >
                <Box className="stat-sheen" sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '38%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)',
                    pointerEvents: 'none',
                    zIndex: 2,
                }} />

                <Box className="stat-glow" sx={{
                    position: 'absolute',
                    top: -70,
                    right: -70,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(color, 0.3)}, transparent 70%)`,
                    opacity: 0.35,
                    transition: `all 0.6s cubic-bezier(0.22,1,0.36,1)`,
                    pointerEvents: 'none',
                }} />

                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.05,
                    backgroundImage: `radial-gradient(${color} 0.5px, transparent 0.5px)`,
                    backgroundSize: '16px 16px',
                    maskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                {React.cloneElement(icon, { 
                    sx: { 
                        position: 'absolute', 
                        right: -8, 
                        bottom: -12, 
                        fontSize: 110, 
                        color: color,
                        opacity: 0.07,
                        transform: 'rotate(-12deg) scale(1.2)',
                        pointerEvents: 'none',
                    } 
                })}

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box className="stat-pod" sx={glassPod(color, 44, '14px')}>
                            {React.cloneElement(icon, { sx: { fontSize: 20 } })}
                        </Box>
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                            fontWeight: 700,
                            fontSize: 'clamp(1.8rem, 3.6vw, 2.4rem)',
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            background: `linear-gradient(145deg, ${palette.ink} 20%, ${color})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            display: 'inline-block',
                            mb: 0.25,
                        }}
                    >
                        {value}
                    </Typography>

                    <Typography
                        sx={{
                            color: palette.inkSoft,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            fontSize: '0.6rem',
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ color: palette.inkLight, mt: 0.5, fontSize: '0.65rem' }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

// ============================================
// GLASS AD CARD
// ============================================

const GlassAdCard = ({ ad, onEdit, onDelete, onToggleStatus }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getStatusColor = () => {
        switch(ad.status) {
            case 'active': return palette.success;
            case 'disabled': return palette.danger;
            case 'paused': return palette.warning;
            case 'expired': return palette.danger;
            default: return palette.inkSoft;
        }
    };

    const getStatusLabel = () => {
        switch(ad.status) {
            case 'active': return 'Active';
            case 'disabled': return 'Disabled';
            case 'paused': return 'Paused';
            case 'expired': return 'Expired';
            default: return 'Draft';
        }
    };

    const getAdTypeIcon = () => {
        switch(ad.type) {
            case 'banner': return <ImageIcon sx={{ fontSize: 14 }} />;
            case 'video': return <VideoCallIcon sx={{ fontSize: 14 }} />;
            case 'link': return <LinkIcon sx={{ fontSize: 14 }} />;
            default: return <StorefrontIcon sx={{ fontSize: 14 }} />;
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const statusColor = getStatusColor();

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            whileHover={{ y: -4 }}
            style={{ height: '100%' }}
        >
            <Paper
                sx={{
                    ...glassCard({ level: 2, radius: '18px', accent: statusColor }),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:hover': {
                        ...glassLift(3, statusColor),
                    },
                }}
            >
                <Box className="stat-sheen" sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '38%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)',
                    pointerEvents: 'none',
                    zIndex: 3,
                }} />

                <Box sx={{
                    height: '3px',
                    background: `linear-gradient(90deg, ${statusColor}, ${rgba(statusColor, 0.35)}, transparent)`,
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 2,
                }} />

                {/* Media Preview */}
                <Box sx={{ position: 'relative', height: 160, bgcolor: palette.canvas, overflow: 'hidden' }}>
                    {ad.type === 'banner' && ad.mediaUrl ? (
                        <img
                            src={ad.mediaUrl}
                            alt={ad.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    ) : ad.type === 'video' && ad.mediaUrl ? (
                        <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#000' }}>
                            <video
                                src={ad.mediaUrl}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                muted
                                loop
                                playsInline
                            />
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                borderRadius: '50%',
                                width: 48,
                                height: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <PlayCircleIcon sx={{ fontSize: 32, color: '#fff' }} />
                            </Box>
                        </Box>
                    ) : ad.type === 'link' ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            bgcolor: rgba(palette.rust, 0.05),
                        }}>
                            <LinkIcon sx={{ fontSize: 48, color: palette.rust, opacity: 0.3 }} />
                            <Typography variant="caption" sx={{ color: palette.inkSoft, mt: 1 }}>
                                {ad.linkUrl || 'No link provided'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}>
                            <StorefrontIcon sx={{ fontSize: 48, color: palette.inkSoft, opacity: 0.3 }} />
                            <Typography variant="caption" sx={{ color: palette.inkSoft, mt: 1 }}>
                                No media uploaded
                            </Typography>
                        </Box>
                    )}
                    <Chip
                        icon={getAdTypeIcon()}
                        label={ad.type || 'banner'}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            ...glassPill(palette.inkSoft),
                            bgcolor: 'rgba(255,255,255,0.9)',
                            fontWeight: 600,
                            fontSize: '0.55rem',
                            height: 22,
                        }}
                    />
                    <Chip
                        label={getStatusLabel()}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            ...glassPill(statusColor, true),
                            fontWeight: 600,
                            fontSize: '0.55rem',
                            height: 22,
                        }}
                    />
                </Box>

                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.95rem', mb: 0.5, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                {ad.title || 'Untitled Ad'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', noWrap: true }}>
                                {ad.companyName || 'Unknown Company'}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleClick} sx={{ color: palette.inkSoft }}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {ad.description && (
                        <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.8rem', mt: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {ad.description}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Chip
                            icon={<CalendarIcon sx={{ fontSize: 12 }} />}
                            label={`Start: ${formatDate(ad.startDate)}`}
                            size="small"
                            sx={{ ...glassPill(palette.inkFaint), height: 20, fontSize: '0.5rem' }}
                        />
                        <Chip
                            icon={<TimerIcon sx={{ fontSize: 12 }} />}
                            label={`End: ${formatDate(ad.endDate)}`}
                            size="small"
                            sx={{ ...glassPill(palette.inkFaint), height: 20, fontSize: '0.5rem' }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Chip
                            icon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                            label={`${ad.impressions || 0} impressions`}
                            size="small"
                            sx={{ ...glassPill(palette.pine), height: 20, fontSize: '0.5rem' }}
                        />
                        <Chip
                            icon={<TrendingUpIcon sx={{ fontSize: 12 }} />}
                            label={`${ad.clicks || 0} clicks`}
                            size="small"
                            sx={{ ...glassPill(palette.rust), height: 20, fontSize: '0.5rem' }}
                        />
                        {ad.ctr && (
                            <Chip
                                label={`CTR: ${ad.ctr}%`}
                                size="small"
                                sx={{ ...glassPill(palette.gold), height: 20, fontSize: '0.5rem' }}
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 1.5, borderTop: `1px solid ${palette.hairline}` }}>
                        {ad.isActive ? (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.success, animation: 'tfPulse 2s infinite' }} />
                        ) : (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.inkSoft }} />
                        )}
                        <Typography variant="caption" sx={{ color: ad.isActive ? palette.success : palette.inkSoft, fontWeight: 600 }}>
                            {ad.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                    </Box>
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{ paper: {
                        sx: {
                            ...glassCard({ level: 3, radius: '14px' }),
                            minWidth: 160,
                        }
                    } }}
                >
                    <MenuItem onClick={() => { handleClose(); onEdit(ad); }} sx={{ gap: 1.5 }}>
                        <EditIcon sx={{ fontSize: 16, color: palette.gold }} /> Edit
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); onToggleStatus(ad); }} sx={{ gap: 1.5 }}>
                        {ad.isActive ? (
                            <>
                                <VisibilityOffIcon sx={{ fontSize: 16, color: palette.warning }} /> Pause
                            </>
                        ) : (
                            <>
                                <VisibilityIcon sx={{ fontSize: 16, color: palette.success }} /> Activate
                            </>
                        )}
                    </MenuItem>
                    <Divider sx={{ borderColor: palette.hairline }} />
                    <MenuItem onClick={() => { handleClose(); onDelete(ad); }} sx={{ color: palette.danger, gap: 1.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} /> Delete
                    </MenuItem>
                </Menu>
            </Paper>
        </motion.div>
    );
};

// ============================================
// MAIN ADS MANAGEMENT COMPONENT
// ============================================

const AdsManagement = () => {
    console.log('📊 AdsManagement component rendered!');
    const navigate = useNavigate();
    const { currentUser, userData, companyData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [ads, setAds] = useState([]);
    const [filteredAds, setFilteredAds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'banner',
        mediaUrl: '',
        linkUrl: '',
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        isActive: false,
        budget: 0,
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(6);

    const identityCompanyId = resolveCompanyId({ companyData, userData, currentUser });
    const [companyId, setCompanyId] = useState(identityCompanyId || null);
    const [companyContextLoading, setCompanyContextLoading] = useState(true);
    const [companyContextError, setCompanyContextError] = useState('');
    const companyName = companyData?.companyName || userData?.companyName || 'Your Company';

    useEffect(() => {
        let active = true;
        const resolveContext = async () => {
            setCompanyContextLoading(true);
            setCompanyContextError('');
            try {
                const resolved = await resolveAuthenticatedCompanyId(identityCompanyId || null);
                if (active) setCompanyId(resolved);
            } catch (error) {
                console.error('Ads company context resolution failed:', error);
                if (active) {
                    setCompanyId(null);
                    setCompanyContextError(friendlyError(error, 'Your company could not be verified.'));
                }
            } finally {
                if (active) setCompanyContextLoading(false);
            }
        };
        resolveContext();
        return () => { active = false; };
    }, [identityCompanyId, currentUser?.uid]);

    // ============================================
    // FETCH ADS
    // ============================================

    const fetchAds = async () => {
        if (!companyId) {
            setAds([]);
            setFilteredAds([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const adsRef = collection(db, 'ads');
            const q = query(adsRef, where('companyId', '==', companyId));
            const snapshot = await getDocs(q);
            const adsList = [];
            snapshot.forEach((doc) => {
                { const row = { id: doc.id, ...doc.data() }; const legacy = ['pending', 'approved', 'rejected'].includes(String(row.status || row.approvalStatus || '').toLowerCase()); adsList.push(legacy ? { ...row, status: row.isActive === false ? 'paused' : 'active', isActive: row.isActive !== false } : row); }
            });
            adsList.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                return dateB - dateA;
            });
            setAds(adsList);
            setFilteredAds(adsList);
        } catch (error) {
            console.error('Error fetching ads:', error);
            toast.error('Failed to load ads');
        } finally {
            setLoading(false);
        }
    };

    // ============================================
    // HANDLE FILE UPLOAD
    // ============================================

    const handleFileUpload = async (file) => {
        if (!file) return null;
        
        setUploading(true);
        setUploadProgress(0);
        
        try {
            const kind = formData.type === 'video' ? 'video' : 'image';
            validateUploadFile(file, { kind, maxBytes: kind === 'video' ? UPLOAD_LIMITS.video : UPLOAD_LIMITS.image });
            const asset = await uploadFile({
                companyId,
                scope: 'company',
                file,
                kind,
                maxBytes: kind === 'video' ? UPLOAD_LIMITS.video : UPLOAD_LIMITS.image,
                folder: 'ads',
                onProgress: (progress) => setUploadProgress(progress)
            });
            setUploadProgress(100);
            return asset;
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(friendlyError(error, 'Failed to upload file'));
            return null;
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // ============================================
    // HANDLE SAVE AD
    // ============================================

    const handleSaveAd = async () => {
        try {
            if (companyContextLoading) {
                toast.error('Company details are still loading. Please try again in a moment.');
                return;
            }
            if (!companyId) {
                toast.error(companyContextError || 'Your company could not be verified. Refresh or sign in again.');
                return;
            }
            if (!formData.title) {
                toast.error('Please enter a title');
                return;
            }

            let mediaUrl = formData.mediaUrl;
            let mediaAsset = selectedAd?.mediaAsset || null;
            
            if (mediaFile) {
                const uploadedAsset = await handleFileUpload(mediaFile);
                if (uploadedAsset) {
                    mediaAsset = uploadedAsset;
                    mediaUrl = uploadedAsset.url;
                } else {
                    toast.error('Failed to upload media');
                    return;
                }
            }

            let safeLinkUrl = '';
            try { safeLinkUrl = normalizeSafeWebUrl(formData.linkUrl); }
            catch (urlError) { toast.error(friendlyError(urlError, 'Enter a valid advertisement link.')); return; }

            const adData = {
                title: formData.title,
                description: formData.description || '',
                type: formData.type || 'banner',
                mediaUrl: mediaUrl || '',
                mediaAsset: mediaAsset || null,
                linkUrl: safeLinkUrl,
                companyId: companyId,
                companyName: companyName,
                startDate: formData.startDate || new Date(),
                endDate: formData.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                budget: formData.budget || 0,
                requestedActive: Boolean(formData.isActive),
                isActive: Boolean(formData.isActive),
                status: formData.isActive ? 'active' : 'paused',
                moderationStatus: 'clear',
                publishedAt: serverTimestamp(),
                impressions: 0,
                clicks: 0,
                ctr: 0,
                updatedAt: serverTimestamp(),
            };

            if (editMode && selectedAd) {
                if (String(selectedAd.companyId || '') !== String(companyId || '')) throw new Error('You cannot edit another company’s advertisement.');
                await updateDoc(doc(db, 'ads', selectedAd.id), adData);
                toast.success('Ad updated successfully!');
            } else {
                adData.createdAt = serverTimestamp();
                adData.createdBy = currentUser?.uid;
                await addDoc(collection(db, 'ads'), adData);
                toast.success('Ad created successfully!');
            }

            setOpenDialog(false);
            resetForm();
            fetchAds();
        } catch (error) {
            console.error('Error saving ad:', error);
            toast.error('Failed to save ad');
        }
    };

    // ============================================
    // HANDLE DELETE AD
    // ============================================

    const handleDeleteAd = async (ad) => {
        if (String(ad?.companyId || '') !== String(companyId || '')) {
            toast.error('You cannot delete another company’s advertisement.');
            return;
        }
        if (!window.confirm('Are you sure you want to delete this ad?')) return;
        try {
            if (ad.mediaAsset) {
                try {
                    await deleteFile({ companyId, asset: ad.mediaAsset });
                } catch (error) {
                    console.warn('Error deleting media:', error);
                }
            }
            await deleteDoc(doc(db, 'ads', ad.id));
            toast.success('Ad deleted successfully!');
            fetchAds();
        } catch (error) {
            console.error('Error deleting ad:', error);
            toast.error('Failed to delete ad');
        }
    };

    // ============================================
    // HANDLE TOGGLE STATUS
    // ============================================

    const handleToggleStatus = async (ad) => {
        try {
            const currentStatus = String(ad.status || '').toLowerCase();
            if (['disabled', 'expired'].includes(currentStatus)) {
                toast.error(currentStatus === 'disabled' ? 'This advertisement was disabled by TELCOFAIR.' : 'This advertisement has expired.');
                return;
            }
            const newStatus = !ad.isActive;
            await updateDoc(doc(db, 'ads', ad.id), {
                isActive: newStatus,
                status: newStatus ? 'active' : 'paused',
                requestedActive: newStatus,
                updatedAt: serverTimestamp(),
            });
            toast.success(`Ad ${newStatus ? 'activated' : 'paused'} successfully!`);
            fetchAds();
        } catch (error) {
            console.error('Error toggling ad status:', error);
            toast.error('Failed to update ad status');
        }
    };

    // ============================================
    // HANDLE EDIT AD
    // ============================================

    const handleEditAd = (ad) => {
        setEditMode(true);
        setSelectedAd(ad);
        setFormData({
            title: ad.title || '',
            description: ad.description || '',
            type: ad.type || 'banner',
            mediaUrl: ad.mediaUrl || '',
            linkUrl: ad.linkUrl || '',
            startDate: ad.startDate?.toDate?.() || new Date(ad.startDate) || new Date(),
            endDate: ad.endDate?.toDate?.() || new Date(ad.endDate) || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            isActive: ad.isActive || false,
            budget: ad.budget || 0,
        });
        setMediaPreview(ad.mediaUrl || null);
        setMediaFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setOpenDialog(true);
    };

    // ============================================
    // RESET FORM
    // ============================================

    const resetForm = () => {
        setEditMode(false);
        setSelectedAd(null);
        setFormData({
            title: '',
            description: '',
            type: 'banner',
            mediaUrl: '',
            linkUrl: '',
            startDate: new Date(),
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            isActive: false,
            budget: 0,
        });
        setMediaPreview(null);
        setMediaFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ============================================
    // HANDLE MEDIA SELECT
    // ============================================

    const handleMediaSelect = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const kind = formData.type === 'video' ? 'video' : 'image';
            validateUploadFile(file, { kind, maxBytes: kind === 'video' ? UPLOAD_LIMITS.video : UPLOAD_LIMITS.image });
            setMediaFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setMediaPreview(e.target.result);
            reader.readAsDataURL(file);
        } catch (error) {
            setMediaFile(null);
            setMediaPreview(null);
            toast.error(friendlyError(error, 'Unsupported media file.'));
        }
    };

    const handleRemoveMedia = () => {
        setMediaPreview(null);
        setMediaFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ============================================
    // FILTER ADS
    // ============================================

    useEffect(() => {
        let filtered = [...ads];
        
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(ad =>
                ad.title?.toLowerCase().includes(searchLower) ||
                ad.description?.toLowerCase().includes(searchLower) ||
                ad.companyName?.toLowerCase().includes(searchLower)
            );
        }
        
        if (filterType !== 'all') {
            filtered = filtered.filter(ad => ad.type === filterType);
        }
        
        if (filterStatus !== 'all') {
            if (filterStatus === 'active') {
                filtered = filtered.filter(ad => ad.isActive && ad.status === 'active');
            } else if (filterStatus === 'disabled') {
                filtered = filtered.filter(ad => ad.status === 'disabled');
            } else if (filterStatus === 'paused') {
                filtered = filtered.filter(ad => !ad.isActive && ad.status !== 'expired');
            } else if (filterStatus === 'expired') {
                filtered = filtered.filter(ad => ad.status === 'expired');
            }
        }
        
        setFilteredAds(filtered);
        setPage(0);
    }, [ads, searchTerm, filterType, filterStatus]);

    // ============================================
    // REAL-TIME LISTENER
    // ============================================

    useEffect(() => {
        if (!companyId) {
            setAds([]);
            setFilteredAds([]);
            return undefined;
        }
        fetchAds();

        const adsRef = collection(db, 'ads');
        const q = query(adsRef, where('companyId', '==', companyId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const adsList = [];
            snapshot.forEach((doc) => {
                { const row = { id: doc.id, ...doc.data() }; const legacy = ['pending', 'approved', 'rejected'].includes(String(row.status || row.approvalStatus || '').toLowerCase()); adsList.push(legacy ? { ...row, status: row.isActive === false ? 'paused' : 'active', isActive: row.isActive !== false } : row); }
            });
            adsList.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                return dateB - dateA;
            });
            setAds(adsList);
            setFilteredAds(adsList);
        }, (error) => {
            console.warn('Ads listener error:', error);
        });

        return () => unsubscribe();
    }, [companyId]);

    // ============================================
    // STATS
    // ============================================

    const stats = {
        total: ads.length,
        active: ads.filter(a => a.isActive && a.status === 'active').length,
        disabled: ads.filter(a => a.status === 'disabled').length,
        paused: ads.filter(a => !a.isActive && a.status !== 'expired').length,
        expired: ads.filter(a => a.status === 'expired').length,
        impressions: ads.reduce((sum, a) => sum + (a.impressions || 0), 0),
        clicks: ads.reduce((sum, a) => sum + (a.clicks || 0), 0),
    };

    // ============================================
    // RENDER
    // ============================================

    const paginatedAds = filteredAds.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box className="erp-page-shell erp-internal-page tf-ads-page" sx={{
            minHeight: '100vh',
            position: 'relative',
            bgcolor: palette.canvas,
            fontFamily: "'Inter', sans-serif",
            overflow: 'hidden',
        }}>
            <style>
                {`
                    @keyframes tfPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
                    @keyframes tfFloat { 0%, 100% { transform: translate(0, 0) rotate(0deg); } 50% { transform: translate(-10px, -10px) rotate(5deg); } }
                    @keyframes tfAurora { 0% { background-position: 0% 0%; } 50% { background-position: 100% 60%; } 100% { background-position: 0% 0%; } }
                    @keyframes tfGrain { 0% { transform: translate(0, 0); } 20% { transform: translate(-2%, 2%); } 40% { transform: translate(2%, -1%); } 60% { transform: translate(-1%, -2%); } 80% { transform: translate(1%, 1%); } 100% { transform: translate(0, 0); } }
                    @keyframes shimmerRule { 0%, 100% { opacity: 0.7; width: 40px; } 50% { opacity: 1; width: 56px; } }
                    @keyframes tfSheen { 0% { transform: translateX(-130%) skewX(-18deg); } 100% { transform: translateX(320%) skewX(-18deg); } }
                `}
            </style>

            
            <Box sx={{ position: 'relative', zIndex: 2 }}><Header /></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    px: { xs: 1.25, sm: 2, lg: 2.5 },
                    py: { xs: 1.1, md: 1.35 },
                    width: '100%',
                    maxWidth: '1780px',
                    mx: 'auto',
                }}
            >
                {/* Header Section */}
                <Box>
                    <Box className="erp-page-hero tf-dashboard-reference-hero"
                        sx={{
                            ...glassCard({ level: 3, radius: '18px', accent: palette.rust }),
                            p: { xs: 1.8, md: 2.2 },
                            mb: 1.45,
                            overflow: 'hidden',
                            position: 'relative',
                            minHeight: { xs: 118, md: 132 },
                            color: '#FFFFFF',
                            background: `linear-gradient(118deg, ${palette.rust} 0%, ${palette.rust} 48%, ${palette.pine} 100%)`,
                            border: '1px solid rgba(255,255,255,.22)',
                            boxShadow: `0 18px 44px ${alpha(palette.rust, .16)}, 0 4px 12px rgba(25,57,78,.08)`,
                        }}
                    >
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: '6%',
                            right: '6%',
                            height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${palette.rust}, ${palette.gold}, ${palette.pine}, transparent)`,
                            backgroundSize: '300% 100%',
                            animation: 'tfAurora 11s linear infinite',
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{
                            position: 'absolute',
                            top: -120,
                            right: -120,
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${palette.rust}20, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{
                            position: 'absolute',
                            bottom: -80,
                            left: -80,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${palette.gold}15, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, position: 'relative', zIndex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Box sx={glassPod(palette.rust, 56, '16px')}>
                                    <StorefrontIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: { xs: '1.45rem', md: '1.9rem' },
                                        color: getGradientContrastText(palette.rust, palette.rustDeep),
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.15,
                                    }}>
                                        Ad Campaigns
                                    </Typography>
                                    <Typography sx={{ color: getGradientContrastText(palette.rust, palette.rustDeep), opacity: .82, fontSize: '0.78rem', mt: 0.25 }}>
                                        Create and manage your advertising campaigns
                                    </Typography>
                                    <SectionRule color={palette.rust} width={60} />
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon sx={{ animation: loading ? 'tfPulse 1s linear infinite' : 'none' }} />}
                                    onClick={fetchAds}
                                    disabled={loading}
                                    sx={{ color: getGradientContrastText(palette.rust, palette.rustDeep), borderColor: 'rgba(255,255,255,.34)', background: 'rgba(255,255,255,.16)', '&:hover': { background: 'rgba(255,255,255,.26)', borderColor: 'rgba(255,255,255,.54)', color: getGradientContrastText(palette.rust, palette.rustDeep) } }}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => { resetForm(); setOpenDialog(true); }}
                                    disabled={companyContextLoading || !companyId}
                                    sx={{ ...glassButton(palette.rust, palette.rustDeep) }}
                                >
                                    Create Ad
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {companyContextError && (
                    <Alert severity="error" sx={{ mb: 1.5, borderRadius: '14px' }}>
                        {companyContextError} Refresh the page or sign in again before creating advertisements.
                    </Alert>
                )}
                {companyContextLoading && (
                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: '14px' }}>
                        Verifying your company workspace before media uploads…
                    </Alert>
                )}

                {/* Stats Cards */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.35,
                    mb: 1.7,
                    width: '100%',
                }}>
                    {[
                        { title: 'Total Ads', value: stats.total, icon: <StorefrontIcon />, color: palette.rust, subtitle: 'All campaigns' },
                        { title: 'Active', value: stats.active, icon: <CheckCircleIcon />, color: palette.success, subtitle: 'Live campaigns' },
                        { title: 'Paused', value: stats.paused, icon: <VisibilityOffIcon />, color: palette.warning, subtitle: 'Not currently live' },
                        { title: 'Impressions', value: stats.impressions, icon: <VisibilityIcon />, color: palette.blue, subtitle: 'Total views' },
                    ].map((stat, index) => (
                        <GlassStatCard key={stat.title} {...stat} delay={index * 0.06} />
                    ))}
                </Box>

                {/* Search & Filters */}
                <Paper
                    elevation={0}
                    sx={{
                        ...glassCard({ level: 2, radius: '20px', accent: palette.gold }),
                        p: { xs: 1.7, md: 1.9 },
                        mb: 1.7,
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search ads..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            sx={{ flex: 1, minWidth: '200px' }}
                            slotProps={{ input: {
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: palette.inkSoft, fontSize: 20 }} /></InputAdornment>,
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    ...glassWell('10px'),
                                    fontFamily: "'Inter', sans-serif",
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                }
                            } }}
                        />
                        
                        <FormControl size="small" sx={{ minWidth: '140px' }}>
                            <InputLabel sx={{ fontFamily: "'Inter', sans-serif" }}>Type</InputLabel>
                            <Select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                label="Type"
                                sx={{
                                    ...glassWell('10px'),
                                    fontFamily: "'Inter', sans-serif",
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                }}
                            >
                                <MenuItem value="all" sx={{ fontFamily: "'Inter', sans-serif" }}>All Types</MenuItem>
                                <MenuItem value="banner" sx={{ fontFamily: "'Inter', sans-serif" }}>Banner</MenuItem>
                                <MenuItem value="video" sx={{ fontFamily: "'Inter', sans-serif" }}>Video</MenuItem>
                                <MenuItem value="link" sx={{ fontFamily: "'Inter', sans-serif" }}>Link</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: '140px' }}>
                            <InputLabel sx={{ fontFamily: "'Inter', sans-serif" }}>Status</InputLabel>
                            <Select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                label="Status"
                                sx={{
                                    ...glassWell('10px'),
                                    fontFamily: "'Inter', sans-serif",
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                }}
                            >
                                <MenuItem value="all" sx={{ fontFamily: "'Inter', sans-serif" }}>All Status</MenuItem>
                                <MenuItem value="active" sx={{ fontFamily: "'Inter', sans-serif" }}>Active</MenuItem>
                                <MenuItem value="disabled" sx={{ fontFamily: "'Inter', sans-serif" }}>Disabled by TELCOFAIR</MenuItem>
                                <MenuItem value="paused" sx={{ fontFamily: "'Inter', sans-serif" }}>Paused</MenuItem>
                                <MenuItem value="expired" sx={{ fontFamily: "'Inter', sans-serif" }}>Expired</MenuItem>
                            </Select>
                        </FormControl>

                        {(searchTerm || filterType !== 'all' || filterStatus !== 'all') && (
                            <Button
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterType('all');
                                    setFilterStatus('all');
                                }}
                                sx={{
                                    ...glassGhostButton(palette.rust),
                                    fontSize: '0.75rem',
                                }}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                </Paper>

                {/* Ads Grid */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: palette.rust }} />
                    </Box>
                ) : filteredAds.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            ...glassCard({ level: 2, radius: '20px', accent: palette.rust }),
                            p: 6,
                            textAlign: 'center',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={glassPod(palette.inkFaint, 72, '50%')}>
                            <StorefrontIcon sx={{ fontSize: 32, color: '#fff', opacity: 0.8 }} />
                        </Box>
                        <Typography variant="h6" sx={{ color: palette.ink, mt: 2, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700 }}>
                            No Ads Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                            {ads.length === 0 ? 'Create your first ad campaign to get started' : 'Try adjusting your search or filters'}
                        </Typography>
                        {ads.length === 0 && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => { resetForm(); setOpenDialog(true); }}
                                disabled={companyContextLoading || !companyId}
                                sx={{ ...glassButton(palette.rust, palette.rustDeep), mt: 3 }}
                            >
                                Create Ad
                            </Button>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {paginatedAds.map((ad) => (
                                <Grid key={ad.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <GlassAdCard
                                        ad={ad}
                                        onEdit={handleEditAd}
                                        onDelete={handleDeleteAd}
                                        onToggleStatus={handleToggleStatus}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        
                        {filteredAds.length > rowsPerPage && (
                            <TablePagination
                                rowsPerPageOptions={[6, 12, 24]}
                                component="div"
                                count={filteredAds.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                sx={{
                                    mt: 2,
                                    ...glassCard({ level: 1, radius: '14px' }),
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        fontFamily: "'Inter', sans-serif",
                                    },
                                }}
                            />
                        )}
                    </>
                )}
            </Container>

            {/* Create/Edit Ad Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => { setOpenDialog(false); resetForm(); }}
                maxWidth="md"
                fullWidth
                slotProps={{ backdrop: {
                    sx: {
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        backgroundColor: rgba(palette.ink, 0.3),
                    }
                }, ...({ paper: {
                    sx: {
                        ...glassCard({ level: 4, radius: '24px', accent: palette.rust }),
                        overflow: 'hidden',
                    }
                } }) }}
                
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: palette.canvasAlt,
                }}>
                    <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                        <Box sx={glassPod(palette.rust, 40, '12px')}>
                            {editMode ? <EditIcon sx={{ fontSize: 20 }} /> : <AddIcon sx={{ fontSize: 20 }} />}
                        </Box>
                        <Box>
                            <Typography sx={{
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                color: palette.ink,
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                            }}>
                                {editMode ? 'Edit Ad Campaign' : 'Create New Ad Campaign'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                {editMode ? 'Update your existing ad campaign' : 'Launch a new advertising campaign'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => { setOpenDialog(false); resetForm(); }} sx={{ color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, px: 3, pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            fullWidth
                            label="Ad Title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                            placeholder="Enter ad title"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('10px'),
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Inter', sans-serif",
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={3}
                            placeholder="Describe your ad campaign"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('10px'),
                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Inter', sans-serif",
                                }
                            }}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ fontFamily: "'Inter', sans-serif" }}>Ad Type</InputLabel>
                                    <Select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        label="Ad Type"
                                        sx={{
                                            ...glassWell('10px'),
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        }}
                                    >
                                        <MenuItem value="banner" sx={{ fontFamily: "'Inter', sans-serif" }}>Banner Image</MenuItem>
                                        <MenuItem value="video" sx={{ fontFamily: "'Inter', sans-serif" }}>Video</MenuItem>
                                        <MenuItem value="link" sx={{ fontFamily: "'Inter', sans-serif" }}>Link</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Budget ($)"
                                    type="number"
                                    value={formData.budget}
                                    onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                                    slotProps={{ input: {
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    } }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('10px'),
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontFamily: "'Inter', sans-serif",
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {formData.type === 'link' && (
                            <TextField
                                fullWidth
                                label="Link URL"
                                value={formData.linkUrl}
                                onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                                placeholder="https://example.com"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        ...glassWell('10px'),
                                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontFamily: "'Inter', sans-serif",
                                    }
                                }}
                            />
                        )}

                        {(formData.type === 'banner' || formData.type === 'video') && (
                            <Box>
                                <Typography variant="subtitle2" sx={{
                                    color: palette.ink,
                                    fontWeight: 600,
                                    mb: 1,
                                    fontFamily: "'Inter', sans-serif",
                                }}>
                                    {formData.type === 'video' ? 'Video' : 'Image'} Upload
                                </Typography>
                                
                                {mediaPreview ? (
                                    <Box sx={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${palette.hairline}` }}>
                                        {formData.type === 'video' ? (
                                            <video
                                                src={mediaPreview}
                                                controls
                                                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', bgcolor: '#000' }}
                                            />
                                        ) : (
                                            <img
                                                src={mediaPreview}
                                                alt="Preview"
                                                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                            />
                                        )}
                                        <IconButton
                                            onClick={handleRemoveMedia}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'rgba(0,0,0,0.6)',
                                                color: '#fff',
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            ...glassWell('10px'),
                                            border: `2px dashed ${palette.hairline}`,
                                            p: 3,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                borderColor: palette.rust,
                                                bgcolor: `${palette.rust}08`,
                                            }
                                        }}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept={formData.type === 'video' ? 'video/*' : 'image/*'}
                                            onChange={handleMediaSelect}
                                            style={{ display: 'none' }}
                                        />
                                        <FileUploadIcon sx={{ fontSize: 40, color: palette.inkSoft, opacity: 0.5 }} />
                                        <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 1, fontFamily: "'Inter', sans-serif" }}>
                                            Click to upload {formData.type === 'video' ? 'video' : 'image'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                                            {formData.type === 'video' ? 'MP4, WebM, MOV' : 'JPG, PNG, GIF'} • Max 10MB
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={formData.startDate instanceof Date ? formData.startDate.toISOString().split('T')[0] : ''}
                                    onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('10px'),
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontFamily: "'Inter', sans-serif",
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={formData.endDate instanceof Date ? formData.endDate.toISOString().split('T')[0] : ''}
                                    onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('10px'),
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontFamily: "'Inter', sans-serif",
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: palette.success,
                                            '&:hover': { bgcolor: rgba(palette.success, 0.1) },
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            bgcolor: palette.success,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{
                                    color: palette.ink,
                                    fontWeight: 500,
                                    fontFamily: "'Inter', sans-serif",
                                }}>
                                    {formData.isActive ? 'Ad is Active' : 'Ad is Inactive'}
                                </Typography>
                            }
                        />

                        {uploading && (
                            <Box sx={{ width: '100%', mt: 1 }}>
                                <Typography variant="caption" sx={{ color: palette.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                                    Uploading... {uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{
                                        height: 4,
                                        borderRadius: 2,
                                        mt: 0.5,
                                        bgcolor: palette.hairline,
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: palette.rust,
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{
                    borderTop: `1px solid ${palette.hairline}`,
                    p: 2.5,
                    px: 3,
                    gap: 1.5,
                    bgcolor: palette.canvasAlt,
                }}>
                    <Button
                        onClick={() => { setOpenDialog(false); resetForm(); }}
                        sx={{ ...glassGhostButton(palette.inkSoft) }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAd}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={uploading || companyContextLoading || !companyId}
                        sx={{ ...glassButton(palette.rust, palette.rustDeep) }}
                    >
                        {editMode ? 'Update Ad' : 'Create Ad'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdsManagement;
